import {
    Check,
    CheckCircle2,
    KeyRound,
    Loader2,
    RotateCw,
    Save,
    Wallet,
} from "lucide-react"
import moment from "moment"
import React, { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { PixPayment, PixQrSkeleton } from "../../../components/pix"
import { Button, CopyText, Input } from "../../../components/ui"
import { useInvalidateWallet } from "../../../hooks/useWallet"
import api from "../../../utils/axios"
import { Pair, PixInfo } from "../types"
import useAwaitStatusPix from "./await.hook"
import { pairBadge } from "./pairBadge"
import ShareButtons from "./ShareButtons"

type ActivateResponse =
    | {
          status: "ACTIVE"
          validated: number | null
          bypass?: boolean
          source?: "PIX" | "CREDIT"
          balance?: number
      }
    | { status: "PROVISIONED"; pix: PixInfo; info?: string }

/**
 * QR "fake" borrado (placeholder) com um botão de recarregar centralizado.
 * Renderizado quando NÃO há um PIX válido (erro na cobrança ou ainda não gerado):
 * clicar no ícone re-chama a ativação para (re)gerar o PIX.
 */
const BlurredQrPlaceholder: React.FC<{
    onReload: () => void
    loading?: boolean
}> = ({ onReload, loading }) => (
    <div className="relative">
        <div
            aria-hidden="true"
            className="pointer-events-none select-none rounded-lg border-2 border-border bg-white p-4 opacity-50 blur-[6px] grayscale"
        >
            <div
                className="grid gap-[3px]"
                style={{ width: 140, height: 140, gridTemplateColumns: "repeat(9, minmax(0, 1fr))" }}
            >
                {Array.from({ length: 81 }, (_, i) => (
                    <div
                        key={i}
                        className={(i * 7919) % 5 < 2 ? "bg-black" : ""}
                    />
                ))}
            </div>
        </div>
        <button
            type="button"
            onClick={onReload}
            disabled={loading}
            aria-label="Gerar PIX novamente"
            title="Gerar PIX novamente"
            className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-fg shadow-lg transition hover:bg-primary/90 active:scale-95 disabled:opacity-70"
        >
            {loading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
                <RotateCw className="h-6 w-6" />
            )}
        </button>
    </div>
)

/** Uma linha de par senha→link sob um code (plano §7.1/§7.2/§7.3, Story 3.5). */
const PairRow: React.FC<{
    code: string
    pair: Pair
    onPairChange: (updated: Pair) => void
    /** Abrir já em modo ativação (dispara a cobrança PIX automaticamente). */
    autoActivate?: boolean
}> = ({ code, pair, onPairChange, autoActivate }) => {
    const invalidateWallet = useInvalidateWallet()
    const [activating, setActivating] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Ativação por crédito (Story 3.10): estado próprio para não confundir com o PIX.
    const [activatingCredit, setActivatingCredit] = useState(false)
    const [creditError, setCreditError] = useState<string | null>(null)
    const [noCredits, setNoCredits] = useState(false)
    // "attempted" liga o placeholder borrado quando ativamos mas não veio PIX válido.
    const [attempted, setAttempted] = useState(false)
    const [pixExpired, setPixExpired] = useState(
        pair.pix?.expiresAt ? pair.pix.expiresAt < Date.now() : false
    )

    // Estado da edição do link (troca de DNS/servidor).
    const [linkDraft, setLinkDraft] = useState(pair.link ?? "")
    const [savingLink, setSavingLink] = useState(false)
    const [linkSaved, setLinkSaved] = useState(false)
    const [linkError, setLinkError] = useState<string | null>(null)

    useEffect(() => {
        setPixExpired(
            pair.pix?.expiresAt ? pair.pix.expiresAt < Date.now() : false
        )
    }, [pair.pix])

    useEffect(() => {
        setLinkDraft(pair.link ?? "")
    }, [pair.link])

    // Polling do PIX (5 s): ao concluir, marca o par como ACTIVE/faturado.
    useAwaitStatusPix(pair.pix, (content) => {
        onPairChange({
            ...pair,
            status: "ACTIVE",
            validated: content.validated ?? pair.validated ?? null,
            pix: null,
        })
    })

    // Função recriada a cada render de propósito: fecha sobre o `pair` atual
    // (evita spread de estado obsoleto após edição de link, etc.).
    async function activate() {
        setActivating(true)
        setError(null)
        setPixExpired(false)
        try {
            const res = await api.post<ActivateResponse>(
                "/device-link/activate",
                { id: pair.id, source: "PIX" }
            )
            if (res.data.status === "ACTIVE") {
                onPairChange({
                    ...pair,
                    status: "ACTIVE",
                    validated: res.data.validated,
                    pix: null,
                })
            } else if (res.data.pix?.qrcode) {
                setAttempted(true)
                onPairChange({ ...pair, pix: res.data.pix })
            } else {
                // Resposta sem qrcode válido → placeholder borrado + recarregar.
                setAttempted(true)
                setError("Não foi possível gerar o PIX. Tente novamente.")
            }
        } catch (err: any) {
            setAttempted(true)
            setError(
                err.response?.data?.error ||
                    "Erro ao criar cobrança Pix. Tente novamente."
            )
        } finally {
            setActivating(false)
        }
    }

    // Ativação por CRÉDITO (Story 3.10): gasta 1 crédito da carteira, sem PIX.
    // Erro 402 (INSUFFICIENT_CREDITS) → mensagem + CTA para a Carteira.
    async function activateWithCredit() {
        setActivatingCredit(true)
        setCreditError(null)
        setNoCredits(false)
        try {
            const res = await api.post<ActivateResponse>(
                "/device-link/activate",
                { id: pair.id, source: "CREDIT" }
            )
            if (res.data.status === "ACTIVE") {
                onPairChange({
                    ...pair,
                    status: "ACTIVE",
                    validated: res.data.validated,
                    pix: null,
                })
                invalidateWallet() // saldo no header
            } else {
                setCreditError("Não foi possível ativar com crédito.")
            }
        } catch (err: any) {
            if (
                err.response?.status === 402 ||
                err.response?.data?.code === "INSUFFICIENT_CREDITS"
            ) {
                setNoCredits(true)
                setCreditError("Saldo de créditos insuficiente.")
            } else {
                setCreditError(
                    err.response?.data?.error ||
                        "Erro ao ativar com crédito. Tente novamente."
                )
            }
        } finally {
            setActivatingCredit(false)
        }
    }

    // Modo ativação: ao abrir o modal via "Ativar Acesso", dispara a cobrança
    // uma única vez (guardado por ref; `activate` fora das deps de propósito).
    const autoFired = useRef(false)
    useEffect(() => {
        if (
            autoActivate &&
            !autoFired.current &&
            pair.status !== "ACTIVE" &&
            !pair.pix
        ) {
            autoFired.current = true
            activate()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoActivate, pair.status, pair.pix])

    async function saveLink() {
        const link = linkDraft.trim()
        if (!link || link === (pair.link ?? "")) return
        setSavingLink(true)
        setLinkError(null)
        setLinkSaved(false)
        try {
            const res = await api.post<{ id: number; link: string }>(
                "/device-link/update-link",
                { id: pair.id, link }
            )
            onPairChange({ ...pair, link: res.data.link })
            setLinkSaved(true)
            setTimeout(() => setLinkSaved(false), 2000)
        } catch (err: any) {
            setLinkError(
                err.response?.data?.error ||
                    "Falha ao salvar o link. Tente novamente."
            )
        } finally {
            setSavingLink(false)
        }
    }

    const isActive = pair.status === "ACTIVE"
    const hasValidPix = !!pair.pix?.qrcode && !pixExpired
    const showBlurred = !isActive && !hasValidPix && (attempted || !!error)
    const linkDirty = linkDraft.trim() !== (pair.link ?? "").trim()

    return (
        <div className="rounded-xl border border-border bg-surface p-4">
            {/* Cabeçalho do par: senha + badge */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-primary/70" />
                    <span className="text-xs font-medium uppercase tracking-wide text-muted">
                        Senha
                    </span>
                    <CopyText value={pair.senha} />
                </div>
                {pairBadge(pair.status, pair.days_remaining)}
            </div>

            {/* Data de criação do par */}
            {pair.createdat && (
                <p className="mt-1 text-xs text-subtle">
                    Criado em{" "}
                    {moment(pair.createdat).format("DD/MM/YYYY HH:mm")}
                </p>
            )}

            {/* Link da playlist, EDITÁVEL (troca de DNS/servidor, Story 3.5) */}
            <div className="mt-3">
                <span className="mb-1 block text-xs font-medium text-muted">
                    Link da playlist
                </span>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                        value={linkDraft}
                        onChange={(e) => setLinkDraft(e.target.value)}
                        placeholder="https://servidor.com/playlist"
                        className="min-w-0 flex-1 font-mono text-xs"
                        invalid={!!linkError}
                    />
                    <div className="flex shrink-0 items-center gap-2">
                        <Button
                            type="button"
                            size="sm"
                            onClick={saveLink}
                            loading={savingLink}
                            disabled={!linkDirty || savingLink}
                            leftIcon={
                                savingLink ? undefined : linkSaved ? (
                                    <Check size={14} />
                                ) : (
                                    <Save size={14} />
                                )
                            }
                        >
                            {linkSaved ? "Salvo" : "Salvar"}
                        </Button>
                        {pair.link && (
                            <CopyText
                                value={pair.link}
                                label=""
                                title="Copiar link da playlist"
                                aria-label="Copiar link da playlist"
                                className="rounded-lg border border-border px-2 py-1.5 text-xs font-normal tracking-normal text-muted hover:text-text"
                            />
                        )}
                    </div>
                </div>
                {linkError && (
                    <p className="mt-1 text-xs text-danger-text">{linkError}</p>
                )}
            </div>

            {/* Compartilhamento code+senha */}
            <div className="mt-3">
                <ShareButtons code={code} senha={pair.senha} />
            </div>

            {/* Ação de ativação / pagamento */}
            <div className="mt-4 border-t border-border pt-3">
                {isActive ? (
                    <div className="flex flex-col items-center py-2 text-center">
                        <CheckCircle2 className="mb-1 h-8 w-8 text-success-text" />
                        <span className="text-sm font-bold text-success-text">
                            Acesso ativado
                        </span>
                        {pair.validated && (
                            <span className="mt-0.5 text-xs text-muted">
                                Válido até{" "}
                                {moment(pair.validated).format(
                                    "DD/MM/YYYY HH:mm"
                                )}
                            </span>
                        )}
                    </div>
                ) : hasValidPix && pair.pix ? (
                    <PixPayment
                        pix={pair.pix}
                        size={140}
                        onExpire={() => setPixExpired(true)}
                        hint="Escaneie o QR Code ou use o copia e cola para ativar este acesso via PIX."
                    />
                ) : activating ? (
                    <PixQrSkeleton size={140} />
                ) : showBlurred ? (
                    <div className="flex flex-col items-center">
                        <BlurredQrPlaceholder
                            onReload={activate}
                            loading={activating}
                        />
                        <span className="mt-3 text-center text-xs text-muted">
                            {pixExpired
                                ? "Este PIX expirou."
                                : "PIX indisponível."}{" "}
                            Toque no ícone para gerar novamente.
                        </span>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {/* Ativar com CRÉDITOS (opção primária sugerida, D-def). */}
                        <Button
                            type="button"
                            variant="success"
                            onClick={activateWithCredit}
                            loading={activatingCredit}
                            leftIcon={
                                activatingCredit ? undefined : (
                                    <Wallet className="h-4 w-4" />
                                )
                            }
                            className="w-full"
                        >
                            {activatingCredit
                                ? "Ativando..."
                                : "Ativar com 1 crédito"}
                        </Button>
                        {creditError && (
                            <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning-text">
                                {creditError}
                                {noCredits && (
                                    <>
                                        {" "}
                                        <Link
                                            to="/credits"
                                            className="font-semibold underline hover:opacity-80"
                                        >
                                            Comprar créditos
                                        </Link>
                                    </>
                                )}
                            </div>
                        )}
                        {/* Alternativa: pagar via PIX. */}
                        <div className="flex items-center justify-end gap-2">
                            <span className="text-xs text-subtle">
                                ou pagar via PIX:
                            </span>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={activate}
                                loading={activating}
                                className="min-w-[140px]"
                            >
                                {activating ? "Ativando..." : "Ativar Acesso"}
                            </Button>
                        </div>
                    </div>
                )}

                {error && (
                    <p className="mt-2 text-center text-sm text-danger-text">
                        {error}
                    </p>
                )}
            </div>
        </div>
    )
}

export default PairRow
